import { NextURL } from "next/dist/server/web/next-url";
import { Url } from "next/dist/shared/lib/router/router";
import { NextRouter, useRouter } from "next/router";
import { globalErrors } from "src/shared/global_errors";
import { UrlObject, UrlWithParsedQuery } from "url";

type NextRouterExt = NextRouter & {
  pushShallow: (url: Partial<UrlWithParsedQuery> | string) => Promise<boolean>;
  replaceShallow: (
    url: Partial<UrlWithParsedQuery> | string
  ) => Promise<boolean>;
  pushSchema: (url: Partial<UrlWithParsedQuery> | string) => Promise<boolean>;
  replaceSchema: (
    url: Partial<UrlWithParsedQuery> | string
  ) => Promise<boolean>;
  convertAsPath: (route: string) => string;
  createAbsoluteUrl: (route: string) => string;
};

export const useRouterExt = () => {
  const router = useRouter() as NextRouterExt;

  // shallow push method to next router
  router.pushShallow = function (url: Partial<UrlWithParsedQuery> | string) {
    const teamSchema = router.query.teamSchema as string;

    const errorMessage = globalErrors.invalidPushWithSchema.errorMessage;
    if (!teamSchema) throw new Error(errorMessage);

    if (typeof url == "string") {
      return this.push({ pathname: url, query: { teamSchema } }, undefined, {
        shallow: true,
      });
    }

    return this.push(
      {
        ...url,
        pathname: url?.pathname,
        query: { ...url?.query, teamSchema },
      },
      undefined,
      { shallow: true }
    );
  };

  // shallow replace method to next router
  router.replaceShallow = function (url: Partial<UrlWithParsedQuery> | string) {
    const teamSchema = router.query.teamSchema as string;

    const errorMessage = globalErrors.invalidPushWithSchema.errorMessage;
    if (!teamSchema) throw new Error(errorMessage);

    if (typeof url == "string") {
      return this.replace({ pathname: url, query: { teamSchema } }, undefined, {
        shallow: true,
      });
    }

    return this.replace(
      {
        ...url,
        pathname: url?.pathname,
        query: { ...url?.query, teamSchema },
      },
      undefined,
      { shallow: true }
    );
  };

  // push method with teamSchema query param always
  router.pushSchema = function (url) {
    const teamSchema = router.query.teamSchema as string;

    const errorMessage = globalErrors.invalidPushWithSchema.errorMessage;
    if (!teamSchema) throw new Error(errorMessage);

    if (typeof url == "string") {
      return this.push({ pathname: url, query: { teamSchema } });
    }
    return this.push({
      ...url,
      pathname: url?.pathname,
      query: { ...url?.query, teamSchema },
    });
  };

  // replace method with teamSchema query param always
  router.replaceSchema = function (url) {
    const teamSchema = router.query.teamSchema as string;

    const errorMessage = globalErrors.invalidPushWithSchema.errorMessage;
    if (!teamSchema) throw new Error(errorMessage);

    if (typeof url == "string") {
      return this.replace({ pathname: url, query: { teamSchema } });
    }
    return this.replace({
      ...url,
      pathname: url?.pathname,
      query: { ...url?.query, teamSchema },
    });
  };

  router.convertAsPath = function (route: string) {
    const teamSchema = router.query.teamSchema as string;
    route = route.replace(`[teamSchema]`, teamSchema);
    return route;
  };

  router.createAbsoluteUrl = function (route: string) {
    const base = window?.location?.origin;
    const path = this.convertAsPath(route);
    return `${base}${path}`;
  };

  return router;
};
